import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { PhoneOff, Phone, MicOff, Mic, User, MessageSquare, Send, X, ChevronDown, Ghost, Mic2, Sparkles, Waves, Moon, Wind, Skull, CircleDashed, Dna, Rocket, Atom, Cpu, Sun, Zap, ArrowLeft, ArrowRight } from 'lucide-react';
import { db, auth, analytics } from '../firebase';
import { logEvent } from 'firebase/analytics';
import { doc, onSnapshot, updateDoc, collection, addDoc, getDocs, deleteDoc, setDoc, getDoc, query, orderBy, serverTimestamp, limit } from 'firebase/firestore';
import * as Tone from 'tone';
import { useIdentity } from '../context/IdentityContext';
import { useRightPanel } from '../context/RightPanelContext';

// Map icon names to Lucide 
// components (keep consistent with CustomVoiceRoomPage)
const VoiceIcons = {
    Ghost, Mic2, Sparkles, Waves, Moon, Wind, Skull, CircleDashed, Dna, Rocket, Atom, Cpu, Sun, Zap, Mic
};

// Deep STUN/TURN Servers for absolute Symmetric NAT Traversal
const baseIceServers = [
    { urls: [
        'stun:stun.l.google.com:19302', 
        'stun:stun1.l.google.com:19302',
        'stun:stun2.l.google.com:19302',
        'stun:stun3.l.google.com:19302',
        'stun:stun4.l.google.com:19302'
    ]},
    { urls: [
        'stun:global.stun.twilio.com:3478',
        'stun:stun.cloudflare.com:3478',
        'stun:stun.services.mozilla.com'
    ]},
    { urls: "stun:stun.relay.metered.ca:80" }
];

if (process.env.REACT_APP_METERED_TURN_USERNAME && process.env.REACT_APP_METERED_TURN_CREDENTIAL) {
    baseIceServers.push({ urls: "turn:standard.relay.metered.ca:443", username: process.env.REACT_APP_METERED_TURN_USERNAME, credential: process.env.REACT_APP_METERED_TURN_CREDENTIAL });
    baseIceServers.push({ urls: "turns:standard.relay.metered.ca:443?transport=tcp", username: process.env.REACT_APP_METERED_TURN_USERNAME, credential: process.env.REACT_APP_METERED_TURN_CREDENTIAL });
}

const servers = {
    iceServers: baseIceServers,
    iceTransportPolicy: "all"
};


// ==================== VOICE CATEGORIES & PRESETS (match CustomVoiceRoomPage) ====================
const VOICE_CATEGORIES = [
    { name: 'Mysterious', voices: [
        { id: 'phantom', name: 'Phantom', icon: 'Ghost', color: '#BD00FF', description: '(Ghostly voice)', effects: { pitchShift: -3, filterType: 'highpass', filterFreq: 800, distortion: 0, reverbWet: 0.5, chorusWet: 0.3 } },
        { id: 'whisper', name: 'Whisper', icon: 'Mic2', color: '#A9A9A9', description: '(ASMR whisper)', effects: { pitchShift: -1, filterType: 'highpass', filterFreq: 2000, distortion: 0, reverbWet: 0.2, chorusWet: 0 } },
        // { id: 'oracle', name: 'Oracle', icon: 'Sparkles', color: '#7B68EE', description: '(Mystical echo)', effects: { pitchShift: -4, filterType: 'bandpass', filterFreq: 700, distortion: 0.15, reverbWet: 0.7, chorusWet: 0.5 } },
    ]},
    { name: 'Female', voices: [
        { id: 'siren', name: 'Siren', icon: 'Waves', color: '#FF69B4', description: '(Smooth feminine)', effects: { pitchShift: 8, filterType: 'highpass', filterFreq: 1200, distortion: 0, reverbWet: 0.15, chorusWet: 0.25 } },
        { id: 'luna', name: 'Luna', icon: 'Moon', color: '#DDA0DD', description: '(Sweet girl)', effects: { pitchShift: 6, filterType: 'highpass', filterFreq: 900, distortion: 0, reverbWet: 0.1, chorusWet: 0.15 } },
        { id: 'nymph', name: 'Nymph', icon: 'Wind', color: '#E0B0FF', description: '(Anime girl)', effects: { pitchShift: 11, filterType: 'highpass', filterFreq: 1500, distortion: 0, reverbWet: 0.1, chorusWet: 0.35 } },
    ]},
    /* { name: 'Dark', voices: [
        // { id: 'deep-shadow', name: 'Deep Shadow', icon: 'Skull', color: '#0BD9E5', description: '(Deep villain)', effects: { pitchShift: -4, filterType: 'lowpass', filterFreq: 1200, distortion: 0.05, reverbWet: 0.1, chorusWet: 0 } },
        // { id: 'void', name: 'Void', icon: 'CircleDashed', color: '#FF4444', description: '(Ultra-deep)', effects: { pitchShift: -6, filterType: 'lowpass', filterFreq: 1000, distortion: 0.1, reverbWet: 0.2, chorusWet: 0 } },
        // { id: 'beast', name: 'Beast', icon: 'Dna', color: '#8B0000', description: '(Creature growl)', effects: { pitchShift: -7, filterType: 'lowpass', filterFreq: 1200, distortion: 0.4, reverbWet: 0.3, chorusWet: 0 } },
    ]}, */
    /* { name: 'Cosmic', voices: [
        // { id: 'interstellar', name: 'Interstellar', icon: 'Rocket', color: '#BD00FF', description: '(Space explorer)', effects: { pitchShift: 2, filterType: 'highpass', filterFreq: 1200, reverbWet: 0.8, chorusWet: 0.4 } },
        // { id: 'nebula', name: 'Nebula', icon: 'Atom', color: '#00D4FF', description: '(Floating gas)', effects: { pitchShift: -2, filterType: 'bandpass', filterFreq: 1000, reverbWet: 0.6, chorusWet: 0.7 } },
    ]}, */
    { name: 'Digital', voices: [
        { id: 'cyborg', name: 'Cyborg', icon: 'Cpu', color: '#00FF88', description: '(Metallic)', effects: { pitchShift: -2, filterType: 'bandpass', filterFreq: 1200, distortion: 0.55, reverbWet: 0.15, chorusWet: 0.3 } },
        { id: 'ethereal', name: 'Ethereal', icon: 'Sun', color: '#FFD700', description: '(Angelic)', effects: { pitchShift: 5, filterType: 'highpass', filterFreq: 1200, distortion: 0, reverbWet: 0.3, chorusWet: 0.5 } },
    ]},
    { name: 'Fun', voices: [
        { id: 'helium', name: 'Helium', icon: 'Zap', color: '#00CED1', description: '(Chipmunk)', effects: { pitchShift: 12, filterType: 'highpass', filterFreq: 1600, distortion: 0, reverbWet: 0, chorusWet: 0.1 } },
        { id: 'original', name: 'Original', icon: 'Mic', color: '#FFFFFF', description: '(Real voice)', effects: null },
    ]},
];

// Flat list for easy lookup and manual picker
const ALL_VOICE_PRESETS = VOICE_CATEGORIES.flatMap(cat => cat.voices);

// Filtered pool for INITIAL random assignment only
const RANDOM_ASSIGNMENT_POOL = ALL_VOICE_PRESETS.filter(v => {
    const category = VOICE_CATEGORIES.find(cat => cat.voices.some(voice => voice.id === v.id));
    return v.effects !== null && 
           v.id !== 'original' && 
           category &&
           category.name !== 'Dark' &&
           ['Mysterious', 'Female', 'Cosmic', 'Fun'].includes(category.name);
});

function getCryptoRandomIndex(maxExclusive) {
    if (maxExclusive <= 1) return 0;
    try {
        const arr = new Uint32Array(1);
        window.crypto.getRandomValues(arr);
        return arr[0] % maxExclusive;
    } catch {
        return Math.floor(Math.random() * maxExclusive);
    }
}

function getRandomVoice(storageKey = 'spark:lastVoiceIds:voicecall') {
    if (RANDOM_ASSIGNMENT_POOL.length === 0) return ALL_VOICE_PRESETS.find(v => v.id === 'original') || ALL_VOICE_PRESETS[0];
    if (RANDOM_ASSIGNMENT_POOL.length === 1) return RANDOM_ASSIGNMENT_POOL[0];

    let recent = [];
    try { recent = JSON.parse(localStorage.getItem(storageKey) || '[]'); } catch {}
    if (!Array.isArray(recent)) recent = [];
    recent = recent.filter(Boolean).slice(-3);

    const candidates = RANDOM_ASSIGNMENT_POOL.filter(v => !recent.includes(v.id));
    const pool = candidates.length > 0 ? candidates : RANDOM_ASSIGNMENT_POOL;
    const picked = pool[getCryptoRandomIndex(pool.length)];

    try {
        const nextRecent = [...recent, picked.id].slice(-3);
        localStorage.setItem(storageKey, JSON.stringify(nextRecent));
    } catch {}

    return picked;
}

// ==================== TONE.JS AUDIO PROCESSING ====================
// Build a Tone.js effect chain and return the output as a MediaStream
function buildToneChain(rawStream, preset, toneNodesRef) {
    // Cleanup any previous chain
    cleanupToneChain(toneNodesRef);

    // If Original voice, return raw stream directly
    if (!preset.effects) {
        return rawStream;
    }

    const fx = preset.effects;
    const ctx = Tone.getContext();

    // 1. Source: Connect raw mic to Tone.js
    const source = ctx.createMediaStreamSource(rawStream);
    const toneSource = new Tone.ToneAudioNode();
    // Wire native source into Tone's graph
    source.connect(toneSource.input || ctx.rawContext.createGain());

    // Use Tone's UserMedia or manual wiring
    // Actually, the cleanest approach: use Tone.js native nodes connected in series

    // Build chain: PitchShift → Filter → Distortion → Chorus → Reverb → Destination
    const nodes = [];

    // PitchShift (the KEY effect for voice anonymity)
    const pitchShift = new Tone.PitchShift({ pitch: fx.pitchShift, windowSize: 0.1 });
    nodes.push(pitchShift);

    // Filter
    const filter = new Tone.Filter({ type: fx.filterType, frequency: fx.filterFreq, rolloff: -24 });
    nodes.push(filter);

    // BitCrusher (if > 0)
    if (fx.bitCrusher > 0) {
        const crusher = new Tone.BitCrusher(fx.bitCrusher);
        nodes.push(crusher);
    }

    // Distortion (if > 0)
    if (fx.distortion > 0) {
        const distortion = new Tone.Distortion(fx.distortion);
        nodes.push(distortion);
    }

    // Vibrato (if > 0)
    if (fx.vibratoWet > 0) {
        const vibrato = new Tone.Vibrato({ frequency: 5, depth: 0.3, wet: fx.vibratoWet });
        nodes.push(vibrato);
    }

    // Chorus (if > 0)
    if (fx.chorusWet > 0) {
        const chorus = new Tone.Chorus({ frequency: 2, delayTime: 3, depth: 0.7, wet: fx.chorusWet }).start();
        nodes.push(chorus);
    }

    // Reverb (if > 0)
    if (fx.reverbWet > 0) {
        const reverb = new Tone.Reverb({ decay: 1.5, wet: fx.reverbWet });
        nodes.push(reverb);
    }

    // Output destination (creates a MediaStream we can feed to WebRTC)
    const destination = ctx.rawContext.createMediaStreamDestination();
    const toneDestGain = new Tone.Gain(1);
    nodes.push(toneDestGain);

    // Connect the raw mic source → first effect
    const sourceGain = ctx.rawContext.createGain();
    sourceGain.gain.value = 1.0;
    source.connect(sourceGain);

    // Convert native node to Tone-compatible and connect chain
    const inputGain = new Tone.Gain(1);
    // Wire: nativeSource → inputGain → pitchShift → filter → ... → destGain
    Tone.connect(sourceGain, inputGain);

    // Chain all Tone nodes together
    let chain = inputGain;
    for (const node of nodes) {
        chain.connect(node);
        chain = node;
    }

    // Connect last node to native destination
    Tone.connect(chain, destination);

    // Store references for cleanup
    toneNodesRef.current = {
        source,
        sourceGain,
        inputGain,
        nodes,
        destination,
    };

    return destination.stream;
}

function cleanupToneChain(toneNodesRef) {
    if (toneNodesRef.current) {
        const { source, sourceGain, inputGain, nodes } = toneNodesRef.current;
        try {
            source?.disconnect();
            sourceGain?.disconnect();
            inputGain?.dispose();
            nodes?.forEach(n => { try { n.dispose(); } catch(e){} });
        } catch(e) {}
        toneNodesRef.current = null;
    }
}

export const VoiceCallPage = () => {
    const { identityMode } = useIdentity();
    const { setIsRightPanelOpen } = useRightPanel();
    const { roomId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const initialRoomData = location.state?.roomData;

    const [roomData, setRoomData] = useState(initialRoomData);
    const [isMuted, setIsMuted] = useState(false);
    const [peerStatus, setPeerStatus] = useState('Connecting...');
    const [timerSeconds, setTimerSeconds] = useState(0);
    const [callActive, setCallActive] = useState(false);
    const [modalConfig, setModalConfig] = useState(null);

    // Voice Effect State
    const [activeVoice, setActiveVoice] = useState(() => getRandomVoice());
    const [showVoicePicker, setShowVoicePicker] = useState(false);
    const [partnerVoice, setPartnerVoice] = useState(null);
    const showChatRef = useRef(true);
    
    // Chat State
    const [showChat, setShowChat] = useState(true);
    const [chatMessages, setChatMessages] = useState([]);
    const [chatInput, setChatInput] = useState('');
    const [unreadCount, setUnreadCount] = useState(0);
    const chatEndRef = useRef(null);

    const localStreamRef = useRef(null);     // Raw mic stream
    const processedStreamRef = useRef(null); // Processed stream sent to WebRTC
    const remoteStreamRef = useRef(null);
    const peerConnectionRef = useRef(null);
    const audioRef = useRef(null);
    const isCaller = useRef(false);
    const iceCandidatesBuffer = useRef([]);
    const toneNodesRef = useRef(null);       // Tone.js effect chain references
    const timerRef = useRef(null);
    
    const myUid = auth.currentUser?.uid;

    // ==================== SCREEN WAKE LOCK ====================
    useEffect(() => {
        let wakeLock = null;
        const requestWakeLock = async () => {
            try {
                if ('wakeLock' in navigator) {
                    wakeLock = await navigator.wakeLock.request('screen');
                }
            } catch (err) {}
        };
        requestWakeLock();
        const handleVisibilityChange = async () => {
            if (wakeLock !== null && document.visibilityState === 'visible') requestWakeLock();
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            if (wakeLock !== null) wakeLock.release().then(() => { wakeLock = null; });
            clearTimeout(timerRef.current);
        };
    }, []);

    // ==================== MAIN WEBRTC EFFECT ====================
    useEffect(() => {
        const unsubscribeAuth = auth.onAuthStateChanged((user) => {
            if (!user) navigate('/login');
            else if (!roomId) navigate('/mode-select', { state: { featureType: 'voice1v1' } });
        });
        
        if (!roomId) return;
        let cancelled = false;

        const roomDocRef = doc(db, 'voiceRooms', roomId);
        const unsubRoom = onSnapshot(roomDocRef, (docSnap) => {
            if (cancelled) return;
            if (!docSnap.exists() || docSnap.data().ended) {
                handleCallCleanup();
                navigate('/find-voice-match-search', { replace: true, state: { autoSearch: true, selectedMode: initialRoomData?.mode || 'global' } });
            } else {
                setRoomData(docSnap.data());
                if (docSnap.data().connectionState === 'connected') {
                    setPeerStatus('Connected');
                    setCallActive(true);
                }
            }
        });

        const initializeCall = async () => {
             const currentUid = auth.currentUser?.uid;
             isCaller.current = (roomData?.user1 === currentUid);
             
             try {
                // Log successful connection intent before Mic access checks
                logEvent(analytics, 'room_connected', { type: '1-1_voice' });
                 
                // Start Tone.js audio context (required for browser autoplay policy)
                await Tone.start();
                
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
                
                if (cancelled) {
                    stream.getTracks().forEach(t => t.stop());
                    return;
                }
                
                localStreamRef.current = stream;

                // Apply default voice effect via Tone.js
                const processed = buildToneChain(stream, activeVoice, toneNodesRef);
                
                // Sync default voice to Firestore
                const voiceField = isCaller.current ? 'user1Voice' : 'user2Voice';
                updateDoc(doc(db, 'voiceRooms', roomId), { [voiceField]: { name: activeVoice.name, icon: activeVoice.icon, color: activeVoice.color } }).catch(() => {});
                processedStreamRef.current = processed;

                const pc = new RTCPeerConnection(servers);
                
                if (cancelled) {
                    stream.getTracks().forEach(t => t.stop());
                    pc.close();
                    return;
                }
                
                peerConnectionRef.current = pc;

                // Push PROCESSED audio tracks into WebRTC
                processed.getTracks().forEach((track) => {
                    pc.addTrack(track, processed);
                });

                const remoteStream = new MediaStream();
                remoteStreamRef.current = remoteStream;

                pc.ontrack = (event) => {
                    event.streams[0].getTracks().forEach((track) => {
                        remoteStream.addTrack(track);
                    });
                    if (audioRef.current) {
                        audioRef.current.srcObject = remoteStream;
                        audioRef.current.play().catch(e => console.error("Autoplay prevents Audio:", e));
                    }
                };

                pc.onconnectionstatechange = () => {
                    if (cancelled) return;
                    if (pc.connectionState === 'connected') {
                        setPeerStatus('Connected');
                        setCallActive(true);
                        updateDoc(doc(db, 'voiceRooms', roomId), { connectionState: 'connected' }).catch(()=>{});
                    } else if (pc.connectionState === 'disconnected') {
                        setPeerStatus('Reconnecting...');
                    } else if (pc.connectionState === 'failed' || pc.connectionState === 'closed') {
                        setPeerStatus('Disconnected');
                        handleEndCall();
                    }
                };

                pc.oniceconnectionstatechange = () => {
                    if (cancelled) return;
                    if (pc.iceConnectionState === 'disconnected') {
                        setPeerStatus('Reconnecting...');
                    } else if (pc.iceConnectionState === 'failed') {
                        if (typeof pc.restartIce === 'function') pc.restartIce();
                    }
                };

                const roomDoc = doc(db, 'voiceRooms', roomId);
                const callerCandidatesCollection = collection(roomDoc, 'callerCandidates');
                const calleeCandidatesCollection = collection(roomDoc, 'calleeCandidates');

                pc.onicecandidate = (event) => {
                    if (event.candidate) {
                        if (isCaller.current) addDoc(callerCandidatesCollection, event.candidate.toJSON());
                        else addDoc(calleeCandidatesCollection, event.candidate.toJSON());
                    }
                };

                if (isCaller.current) {
                    const offerDescription = await pc.createOffer();
                    if (cancelled) return;
                    await pc.setLocalDescription(offerDescription);
                    if (cancelled) return;
                    await updateDoc(roomDoc, { offer: { sdp: offerDescription.sdp, type: offerDescription.type } });
                    if (cancelled) return;

                    onSnapshot(roomDoc, async (snapshot) => {
                        if (cancelled) return;
                        const data = snapshot.data();
                        if (!pc.currentRemoteDescription && data?.answer) {
                            await pc.setRemoteDescription(new RTCSessionDescription(data.answer));
                            iceCandidatesBuffer.current.forEach(c => pc.addIceCandidate(c));
                            iceCandidatesBuffer.current = [];
                        }
                    });

                    onSnapshot(calleeCandidatesCollection, (snapshot) => {
                        if (cancelled) return;
                        snapshot.docChanges().forEach((change) => {
                            if (change.type === 'added') {
                                const candidate = new RTCIceCandidate(change.doc.data());
                                if (pc.remoteDescription) pc.addIceCandidate(candidate);
                                else iceCandidatesBuffer.current.push(candidate);
                            }
                        });
                    });
                } else {
                    onSnapshot(roomDoc, async (snapshot) => {
                        if (cancelled) return;
                        const data = snapshot.data();
                        if (data?.offer && !pc.currentRemoteDescription) {
                            await pc.setRemoteDescription(new RTCSessionDescription(data.offer));
                            iceCandidatesBuffer.current.forEach(c => pc.addIceCandidate(c));
                            iceCandidatesBuffer.current = [];
                            const answerDescription = await pc.createAnswer();
                            await pc.setLocalDescription(answerDescription);
                            await updateDoc(roomDoc, { answer: { type: answerDescription.type, sdp: answerDescription.sdp } });
                        }
                    });

                    onSnapshot(callerCandidatesCollection, (snapshot) => {
                        if (cancelled) return;
                        snapshot.docChanges().forEach((change) => {
                            if (change.type === 'added') {
                                const candidate = new RTCIceCandidate(change.doc.data());
                                if (pc.remoteDescription) pc.addIceCandidate(candidate);
                                else iceCandidatesBuffer.current.push(candidate);
                            }
                        });
                    });
                }
             } catch (e) {
                 console.error("WebRTC Init error:", e);
                 if (!cancelled) setPeerStatus('Failed: ' + e.message);
             }
        };
         
        initializeCall();
         
        const handleWindowClose = () => handleCallCleanup();
        window.addEventListener('beforeunload', handleWindowClose);

        return () => {
            cancelled = true;
            window.removeEventListener('beforeunload', handleWindowClose);
            unsubscribeAuth();
            unsubRoom();
            
            cleanupToneChain(toneNodesRef);
            if (localStreamRef.current) {
                localStreamRef.current.getTracks().forEach(track => track.stop());
                localStreamRef.current = null;
            }
            if (processedStreamRef.current && processedStreamRef.current !== localStreamRef.current) {
                processedStreamRef.current.getTracks().forEach(track => track.stop());
                processedStreamRef.current = null;
            }
            if (peerConnectionRef.current) {
                peerConnectionRef.current.close();
                peerConnectionRef.current = null;
            }
        };
    }, [roomId, navigate]);

    // ==================== CHAT MESSAGES LISTENER ====================
    useEffect(() => {
        if (!roomId) return;
        const messagesRef = collection(db, 'voiceRooms', roomId, 'messages');
        const q = query(messagesRef, orderBy('timestamp', 'asc'));
        let isFirstSnapshot = true;
        
        const unsub = onSnapshot(q, (snapshot) => {
            const msgs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            setChatMessages(msgs);
            
            // Only count new messages after the initial load, and only when chat is closed
            if (!isFirstSnapshot && !showChatRef.current) {
                const newMsgs = snapshot.docChanges().filter(c => c.type === 'added' && c.doc.data().sender !== auth.currentUser?.uid);
                if (newMsgs.length > 0) setUnreadCount(prev => prev + newMsgs.length);
            }
            isFirstSnapshot = false;
        });
        return () => unsub();
    }, [roomId]);

    // Auto-scroll chat
    useEffect(() => {
        if (showChat && chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }, [chatMessages, showChat]);

    // Timer
    useEffect(() => {
        let interval;
        if (callActive) interval = setInterval(() => setTimerSeconds(prev => prev + 1), 1000);
        return () => clearInterval(interval);
    }, [callActive]);

    // ==================== VOICE SWITCHING (Tone.js) ====================
    const switchVoice = (preset) => {
        setActiveVoice(preset);
        setShowVoicePicker(false);
        
        // Sync voice selection to Firestore so partner can see it
        if (roomId) {
            const voiceField = isUser1 ? 'user1Voice' : 'user2Voice';
            updateDoc(doc(db, 'voiceRooms', roomId), { [voiceField]: { name: preset.name, icon: preset.icon, color: preset.color } }).catch(() => {});
        }

        if (!localStreamRef.current || !peerConnectionRef.current) return;

        const pc = peerConnectionRef.current;
        const rawStream = localStreamRef.current;

        // Build new Tone.js effect chain
        const newProcessed = buildToneChain(rawStream, preset, toneNodesRef);
        processedStreamRef.current = newProcessed;

        // Replace the audio track on the live RTCPeerConnection sender
        const audioSender = pc.getSenders().find(s => s.track && s.track.kind === 'audio');
        if (audioSender && newProcessed.getAudioTracks().length > 0) {
            audioSender.replaceTrack(newProcessed.getAudioTracks()[0]);
        }
    };

    // ==================== HANDLERS ====================
    const handleCallCleanup = async () => {
        cleanupToneChain(toneNodesRef);
        if (localStreamRef.current) localStreamRef.current.getTracks().forEach(track => track.stop());
        if (peerConnectionRef.current) peerConnectionRef.current.close();
        try { await updateDoc(doc(db, 'voiceRooms', roomId), { ended: true }); } catch(e) {}
    };

    const handleEndCall = () => {
        handleCallCleanup();
        navigate('/find-voice-match-search', { replace: true, state: { autoSearch: true, selectedMode: roomData?.mode || 'global' } });
    };

    const handleNextCall = () => {
        handleCallCleanup();
        navigate('/find-voice-match-search', { replace: true, state: { autoSearch: true, selectedMode: roomData?.mode || 'global' } });
    };

    const toggleMute = () => {
        const newMuted = !isMuted;
        if (localStreamRef.current) {
            localStreamRef.current.getAudioTracks().forEach(track => { track.enabled = !newMuted; });
        }
        if (peerConnectionRef.current) {
            peerConnectionRef.current.getSenders().forEach(sender => {
                if (sender.track && sender.track.kind === 'audio') sender.track.enabled = !newMuted;
            });
        }
        setIsMuted(newMuted);
        // Sync mute state to Firestore so partner can see it
        if (roomId) {
            const muteField = isUser1 ? 'user1Muted' : 'user2Muted';
            updateDoc(doc(db, 'voiceRooms', roomId), { [muteField]: newMuted }).catch(() => {});
        }
    };

    // Right Panel Sync
    useEffect(() => {
        setIsRightPanelOpen(showChat);
        return () => setIsRightPanelOpen(false);
    }, [showChat, setIsRightPanelOpen]);

    const sendChatMessage = async () => {
        if (!chatInput.trim() || !roomId) return;
        const text = chatInput.trim();
        setChatInput('');
        try {
            await addDoc(collection(db, 'voiceRooms', roomId, 'messages'), {
                sender: auth.currentUser?.uid, text, timestamp: serverTimestamp()
            });
        } catch (e) { console.error('Chat send error:', e); }
    };

    const handleChatKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChatMessage(); } };

    const toggleChat = () => { 
        setShowChat(prev => {
            const next = !prev;
            showChatRef.current = next;
            if (next) setUnreadCount(0);
            return next;
        }); 
    };

    const formatTime = (seconds) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m}:${s < 10 ? '0' : ''}${s}`;
    };

    const isUser1 = roomData?.user1 === auth.currentUser?.uid;
    const partnerAlias = isUser1 ? roomData?.user2Alias : roomData?.user1Alias;
    const partnerCollege = isUser1 ? roomData?.user2College : roomData?.user1College;
    const myAlias = isUser1 ? roomData?.user1Alias : roomData?.user2Alias;
    const myCollege = isUser1 ? roomData?.user1College : roomData?.user2College;
    const partnerVoiceData = isUser1 ? roomData?.user2Voice : roomData?.user1Voice;
    const partnerMuted = isUser1 ? roomData?.user2Muted : roomData?.user1Muted;

    if (identityMode === 'real') {
        return (
            <main className="w-full sm:w-[700px] min-h-screen border-r border-gray-800 flex flex-col items-center justify-center p-8 shrink-0 relative bg-[#0a0a0a]">
                <div className="w-16 h-16 rounded-full bg-[#0BD9E5]/20 text-[#0BD9E5] flex items-center justify-center mb-6">
                    <Phone size={32} />
                </div>
                <h2 className="text-[31px] font-extrabold mb-2 leading-tight text-center text-white">Voice Call</h2>
                <p className="text-gray-500 text-[15px] text-center">Coming soon for Real Identity mode.</p>
            </main>
        );
    }

    // ==================== RENDER ====================
    return (
        <main className="w-full sm:w-[700px] h-screen sm:h-auto sm:min-h-screen border-r border-gray-800 flex flex-col shrink-0 relative overflow-hidden bg-black text-white font-poppins selection:bg-[#0BD9E5]/30">
            
            {/* Background */}
            <div className="absolute inset-0 pointer-events-none z-0">
                <div className="absolute inset-0 transition-opacity duration-1000 opacity-60"
                     style={{ background: 'radial-gradient(circle at 50% 30%, rgba(11,217,229,0.15), transparent 70%)' }} />
            </div>

            <audio ref={audioRef} autoPlay playsInline className="hidden" />

            <div className="relative z-10 flex flex-col h-full">

                <div className="flex-1 flex flex-col items-center justify-center -mt-6">
                    {/* Partner Avatar (big, center) */}
                    <div className="relative mb-10">
                        {callActive && (
                            <div className="absolute inset-0 flex items-center justify-center">
                                <motion.div animate={{ scale: [1, 1.5, 1], opacity: [0.5, 0, 0.5] }} transition={{ duration: 2, repeat: Infinity }} className="absolute w-40 h-40 border rounded-full" style={{ borderColor: activeVoice.color }} />
                                <motion.div animate={{ scale: [1, 2, 1], opacity: [0.2, 0, 0.2] }} transition={{ duration: 2, repeat: Infinity, delay: 0.5 }} className="absolute w-40 h-40 border rounded-full" style={{ borderColor: activeVoice.color }} />
                            </div>
                        )}
                        <div className="w-40 h-40 bg-gray-900 border-2 border-gray-600 shadow-2xl rounded-full relative z-10 flex items-center justify-center overflow-hidden">
                            {partnerAlias ? (
                                <img src={`/avatars/${partnerAlias}.png`} alt="partner avatar" className="w-full h-full object-cover" />
                            ) : (
                                <User className="text-gray-500 w-14 h-14" />
                            )}
                        </div>
                        {partnerMuted && (
                            <div className="absolute top-1 right-1 z-20 w-8 h-8 rounded-full bg-red-500/90 backdrop-blur-sm flex items-center justify-center shadow-lg">
                                <MicOff size={14} className="text-white" />
                            </div>
                        )}
                    </div>

                    {/* Partner Info */}
                    <h2 className="text-3xl font-black mb-1">{partnerAlias || 'Anonymous User'}</h2>
                    
                    {partnerCollege && (
                       <p className="text-[11px] font-medium text-gray-400 tracking-widest uppercase mb-2 border border-gray-700 rounded-full px-3 py-0.5 bg-gray-900 shadow-inner">
                           {partnerCollege}
                       </p>
                    )}

                    {/* Partner's Voice Effect Badge */}
                    {partnerVoiceData && (
                        <div className="flex items-center gap-1.5 mb-2 px-3 py-1 rounded-full bg-gray-900/80 border border-gray-800">
                            {(() => {
                                const Icon = VoiceIcons[partnerVoiceData.icon] || User;
                                return <Icon size={14} style={{ color: partnerVoiceData.color }} />;
                            })()}
                            <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: partnerVoiceData.color }}>{partnerVoiceData.name}</span>
                        </div>
                    )}

                    <p className={`text-sm ${callActive ? 'text-[#0BD9E5]' : 'text-gray-400 animate-pulse'} font-medium`}>
                        {peerStatus}
                    </p>

                    {callActive && (
                        <p className="font-mono text-gray-300 font-bold text-2xl mt-3">
                            {formatTime(timerSeconds)}
                        </p>
                    )}
                </div>

                {/* Call Controls */}
                <div className="pb-12 pt-8 flex items-center justify-center gap-6 bg-gradient-to-t from-black via-black to-transparent">
                    <button 
                        onClick={toggleMute}
                        className={`w-14 h-14 rounded-full flex items-center justify-center transition-all shadow-lg ${isMuted ? 'bg-white text-black' : 'bg-gray-800 text-white hover:bg-gray-700'}`}
                    >
                        {isMuted ? <MicOff size={24} /> : <Mic size={24} />}
                    </button>

                    <button 
                        onClick={toggleChat}
                        className="w-14 h-14 rounded-full bg-gray-800 text-white hover:bg-gray-700 flex items-center justify-center transition-colors shadow-lg relative"
                    >
                        <MessageSquare size={24} />
                        {unreadCount > 0 && (
                            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-[10px] font-bold flex items-center justify-center">
                                {unreadCount > 9 ? '9+' : unreadCount}
                            </span>
                        )}
                    </button>

                    <div className="flex items-center gap-4">
                        <button 
                            onClick={handleEndCall}
                            className="w-20 h-20 rounded-[2.5rem] bg-red-500 hover:bg-red-600 text-white flex items-center justify-center transition-transform hover:scale-105 shadow-[0_0_30px_rgba(239,68,68,0.3)]"
                        >
                            <Phone size={32} />
                        </button>
                    </div>

                    {/* My Identity */}
                    <div className="flex flex-col items-center gap-1">
                        <div className="w-14 h-14 rounded-full bg-gray-800 border-2 border-gray-700 flex items-center justify-center overflow-hidden shadow-lg">
                            {myAlias ? (
                                <img src={`/avatars/${myAlias}.png`} alt="me" className="w-full h-full object-cover" />
                            ) : (
                                <User className="text-gray-500 w-6 h-6" />
                            )}
                        </div>
                        <p className="text-[8px] font-bold text-gray-500 uppercase tracking-wider">{myAlias ? `${myAlias} (YOU)` : 'You'}</p>
                    </div>

                    {/* Switch Voice */}
                    <button 
                        onClick={() => setShowVoicePicker(!showVoicePicker)}
                        className="w-14 h-14 rounded-full bg-gray-800 hover:bg-gray-700 flex items-center justify-center transition-colors border border-gray-700 relative group shadow-lg"
                        style={{ borderColor: activeVoice.color + '50' }}
                    >
                        {(() => {
                            const Icon = VoiceIcons[activeVoice.icon] || User;
                            return <Icon size={22} style={{ color: activeVoice.color, filter: `drop-shadow(0 0 8px ${activeVoice.color}40)` }} />;
                        })()}
                    </button>
                </div>
            </div>

            {/* ==================== VOICE PRESET PICKER ==================== */}
            <AnimatePresence>
                {showVoicePicker && (
                    <motion.div
                        initial={{ y: '100%' }}
                        animate={{ y: 0 }}
                        exit={{ y: '100%' }}
                        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                        className="absolute bottom-0 left-0 right-0 z-50 bg-[#111] border-t border-gray-800 rounded-t-3xl p-6 pb-10"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-bold">Voice Effect</h3>
                            <button onClick={() => setShowVoicePicker(false)} className="text-gray-500 hover:text-white transition-colors">
                                <X size={20} />
                            </button>
                        </div>
                        
                        <div className="max-h-[55vh] overflow-y-auto pr-1 space-y-4" style={{ scrollbarWidth: 'thin', scrollbarColor: '#333 transparent' }}>
                            {VOICE_CATEGORIES.map((category) => (
                                <div key={category.name}>
                                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 mb-2 pl-1">{category.name}</p>
                                    <div className="grid grid-cols-3 gap-2">
                                        {category.voices.map((preset) => (
                                            <div
                                                key={preset.id}
                                                className={`flex flex-col items-center gap-1 p-2.5 rounded-2xl border transition-all cursor-pointer ${
                                                    activeVoice.id === preset.id 
                                                        ? 'scale-[1.03]' 
                                                        : 'border-gray-800 bg-transparent hover:bg-white/5'
                                                }`}
                                                style={{
                                                    borderColor: activeVoice.id === preset.id ? preset.color : undefined,
                                                    backgroundColor: activeVoice.id === preset.id ? preset.color + '15' : undefined,
                                                    boxShadow: activeVoice.id === preset.id ? `0 0 20px ${preset.color}30` : undefined
                                                }}
                                                onClick={() => switchVoice(preset)}
                                            >
                                                {(() => {
                                                    const Icon = VoiceIcons[preset.icon] || User;
                                                    return <Icon size={18} style={{ color: preset.color, filter: `drop-shadow(0 0 8px ${preset.color}40)` }} />;
                                                })()}
                                                <span className="text-[9px] font-bold uppercase tracking-wider leading-tight" style={{ color: activeVoice.id === preset.id ? preset.color : '#9CA3AF' }}>
                                                    {preset.name}
                                                </span>
                                                <span className="text-[7px] text-gray-500 leading-tight text-center">
                                                    {preset.description}
                                                </span>

                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ==================== IN-CALL CHAT PANEL ==================== */}
            {/* ==================== IN-CALL CHAT PANEL ==================== */}
            {(() => {
                const chatPanelContent = (
                    <div className="flex flex-col h-full bg-black/95 backdrop-blur-xl lg:bg-black w-full border-gray-800 lg:border-l-0">
                        {/* Chat Header */}
                        <div className="flex items-center justify-between p-4 border-b border-gray-800 lg:hidden">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center overflow-hidden">
                                    {partnerAlias ? (
                                        <img src={`/avatars/${partnerAlias}.png`} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                        <User size={14} className="text-gray-500" />
                                    )}
                                </div>
                                <div>
                                    <p className="text-sm font-bold">{partnerAlias || 'Anonymous'}</p>
                                    <p className="text-[10px] text-[#0BD9E5] font-medium">{formatTime(timerSeconds)} • On Call</p>
                                </div>
                            </div>
                            <button onClick={() => { setShowChat(false); showChatRef.current = false; }} className="text-gray-500 hover:text-white transition-colors p-2">
                                <X size={20} />
                            </button>
                        </div>
                        
                        {/* Desktop Chat Header */}
                        <div className="hidden lg:flex items-center justify-between p-4 border-b border-gray-800">
                             <h3 className="font-extrabold text-[20px]">In-call Messages</h3>
                             <button onClick={() => { setShowChat(false); showChatRef.current = false; }} className="text-gray-500 hover:text-white transition-colors p-2">
                                 <X size={20} />
                             </button>
                        </div>

                        {/* Chat Messages */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-3">
                            {chatMessages.length === 0 && (
                                <div className="flex flex-col items-center justify-center h-full text-gray-500 text-sm">
                                    <MessageSquare size={32} className="mb-3 opacity-30" />
                                    <p>Send a message during the call</p>
                                </div>
                            )}
                            {chatMessages.map((msg) => {
                                const isMe = msg.sender === auth.currentUser?.uid;
                                const senderAvatar = !isMe && partnerAlias ? `/avatars/${partnerAlias}.png` : null;

                                return (
                                    <div key={msg.id} className={`flex w-full ${isMe ? 'justify-end' : 'justify-start'}`}>
                                        <div className={`flex items-end gap-2 max-w-[85%] ${isMe ? 'flex-row-reverse' : 'flex-row'}`}>
                                            {/* Avatar (For incoming only) */}
                                            {!isMe && (
                                                <div className="w-6 h-6 rounded-full bg-gray-800 flex-shrink-0 flex items-center justify-center overflow-hidden mb-1">
                                                    {senderAvatar ? (
                                                        <img src={senderAvatar} alt="" className="w-full h-full object-cover" />
                                                    ) : (
                                                        <User size={12} className="text-gray-500" />
                                                    )}
                                                </div>
                                            )}

                                            {/* Message Content */}
                                            <div className="flex flex-col">
                                                {!isMe && (
                                                    <p className="text-[10px] text-gray-500 font-bold mb-1 ml-2">{partnerAlias || 'Anonymous'}</p>
                                                )}
                                                <div className={`px-4 py-2.5 rounded-2xl text-sm break-words ${
                                                    isMe 
                                                        ? 'bg-[#0BD9E5] text-black rounded-br-sm' 
                                                        : 'bg-gray-800 text-white rounded-bl-sm'
                                                }`}>
                                                    {msg.text}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                            <div ref={chatEndRef} />
                        </div>

                        {/* Chat Input */}
                        <div className="p-4 border-t border-gray-800">
                            <div className="flex items-center gap-3 bg-gray-900 rounded-2xl px-4 py-2 border border-gray-800 focus-within:border-[#0BD9E5]/50 transition-colors">
                                <input
                                    type="text"
                                    value={chatInput}
                                    onChange={(e) => setChatInput(e.target.value)}
                                    onKeyDown={handleChatKeyDown}
                                    placeholder="Type a message..."
                                    className="flex-1 bg-transparent text-white text-sm outline-none placeholder-gray-500"
                                    autoFocus
                                />
                                <button 
                                    onClick={sendChatMessage}
                                    disabled={!chatInput.trim()}
                                    className="text-[#0BD9E5] hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                    <Send size={20} />
                                </button>
                            </div>
                        </div>
                    </div>
                );

                const portalTarget = document.getElementById('right-panel-chat-container');

                return (
                    <>
                        {/* Mobile Overlay */}
                        <div className="lg:hidden">
                            <AnimatePresence>
                                {showChat && (
                                    <motion.div
                                        initial={{ y: '100%' }}
                                        animate={{ y: 0 }}
                                        exit={{ y: '100%' }}
                                        transition={{ type: 'tween', duration: 0.25, ease: 'easeOut' }}
                                        className="absolute inset-0 z-50 flex flex-col"
                                    >
                                        {chatPanelContent}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                        
                        {/* Desktop Portal */}
                        {showChat && portalTarget && ReactDOM.createPortal(chatPanelContent, portalTarget)}
                    </>
                );
            })()}

        </main>
    );
};

export default VoiceCallPage;
